import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useEppDestroyedOrMissing } from '../../common/hooks/useEppDestroyedOrMissing';
import { contactEmailAddressMsg, contactPhoneNumberMsg, deviceMsg, t } from '../../common/i18n/index.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { formatSum } from '../../common/utils/priceUtils.js';
import { EppRedeemTerminateRequestType } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { Loading } from '../Loading/index.js';
import { DeliveryAddressDetails } from './DeliveryAddressDetails.js';
import { EppDevicePendingActionsDisclaimer } from './EppDevicePendingActionsDisclaimer.js';
import { getFullNameFromAuthenticatedUser } from './eppMaintenanceUtils.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { CompanyInfoResponse, DeliveryAddress, Subscription } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './EppDestroyedOrMissingDevice.scss';

export interface EppDestroyedOrMissingDeviceProps {
  subscription: EmployeeSubscription | Subscription;
  onCancel: () => void;
  companyInfo?: CompanyInfoResponse;
  hasPendingActions: boolean;
}

export enum EppDestroyedOrMissingType {
  MISSING = 'MISSING',
  DESTROYED = 'DESTROYED',
}

const DescriptionText = ({ destroyedPrice }: { destroyedPrice?: number }) => (
  <div className="of-epp-destroyed-or-missing-device__description_text">
    <p>{t.S0V2('Elisa charges the remaining monthly fees for a destroyed or missing device as a one-time fee.')}</p>
    <p>
      {`${t.EBX4('For this device the amount is ')} `}
      <strong>{formatSum(destroyedPrice)}.</strong>
    </p>
    <p>
      {`${t.WZNT('At the same time, the contract period for Elisa Device is suspended.')} `}
      <strong>{t.ABI6('The replacement device will not be sent automatically.')}</strong>
      <br />
      {t.HIOW(`If necessary, order a new device or instruct the employee to order it from the Employee's OmaElisa.`)}
    </p>
  </div>
);

const LostOrDestroyedSelector = ({
  setRequestType,
}: {
  setRequestType: (requestType: EppDestroyedOrMissingType) => void;
}) => (
  <>
    <p className="of-epp-destroyed-or-missing-device__radio_button_title">{t.OIVZ('Issue')}</p>
    <SelectRadio
      name="option"
      items={[
        {
          label: t.FINQ('Device has been destroyed'),
          value: EppDestroyedOrMissingType.DESTROYED,
        },
        {
          label: t.KFGF('Device is lost'),
          value: EppDestroyedOrMissingType.MISSING,
        },
      ]}
      onChange={value => setRequestType(value as EppDestroyedOrMissingType)}
      defaultValue={EppDestroyedOrMissingType.DESTROYED}
    />
  </>
);

const DeviceDetails = ({ subscription, user }: { subscription: Subscription; user?: AuthenticatedUser | null }) => (
  <>
    <div className={dsClass.GRID_ROW}>
      <div
        className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_6} of-epp-destroyed-or-missing-device__device_details-value`}
      >
        <strong>{t.TPVQ(deviceMsg)}</strong>
        {subscription.subscriptionName}
      </div>
      <div
        className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_6} of-epp-destroyed-or-missing-device__device_details-value`}
      >
        <strong>{t.S5PK(contactEmailAddressMsg)}</strong>
        {user?.email || '-'}
      </div>
    </div>
    <div className={dsClass.GRID_ROW}>
      <div
        className={`${dsClass.GRID_COL_XS_4} ${dsClass.GRID_COL_L_6} of-epp-destroyed-or-missing-device__device_details-value`}
      >
        <strong>{t.HUN9(contactPhoneNumberMsg)}</strong>
        {user?.mobile || '-'}
      </div>
    </div>
  </>
);

export const EppDestroyedOrMissingDevice = ({
  subscription,
  onCancel,
  companyInfo,
  hasPendingActions,
}: EppDestroyedOrMissingDeviceProps) => {
  const postEppDestroyedOrMissing = useEppDestroyedOrMissing(subscription);
  const [requestType, setRequestType] = useState(EppDestroyedOrMissingType.DESTROYED);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>();
  const { authenticatedUser } = useAuth();

  const handleSubmit = () => {
    postEppDestroyedOrMissing(requestType, deliveryAddress); // submit will be disabled unless the deliveryAddress is defined
  };

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: { option: requestType },
  });

  if (!companyInfo) {
    return <Loading />;
  } else {
    return (
      <FormProvider {...methods}>
        {hasPendingActions && !methods.formState.isSubmitting ? (
          <EppDevicePendingActionsDisclaimer onClose={onCancel} />
        ) : (
          <div className="of-epp-destroyed-or-missing-device">
            <DescriptionText destroyedPrice={subscription.details?.device?.eppModificationPrices?.destroyed} />
            <LostOrDestroyedSelector setRequestType={setRequestType} />
            <hr />
            <DeviceDetails subscription={subscription} user={authenticatedUser} />
            {requestType === EppRedeemTerminateRequestType.DESTROYED && (
              <DeliveryAddressDetails
                address={companyInfo.address}
                companyName={companyInfo.companyName}
                recipientName={getFullNameFromAuthenticatedUser(authenticatedUser)}
                phoneNumber={authenticatedUser?.mobile || ''}
                setDeliveryAddress={setDeliveryAddress}
                showConfirmAddressCheckbox={true}
                onAddressReady={setIsSubmitEnabled}
                isSupportRequest={false}
              />
            )}
            <hr />
            <ButtonGroupForSubmitAndBack
              className="of-epp-destroyed-or-missing-device__buttons"
              onCancel={onCancel}
              submitDisabled={
                (!isSubmitEnabled || !deliveryAddress) && requestType !== EppDestroyedOrMissingType.MISSING
              }
              onSubmit={() => methods.handleSubmit(handleSubmit)()}
              submitting={methods.formState.isSubmitting}
            />
          </div>
        )}
      </FormProvider>
    );
  }
};
